import { useState } from 'react';
import { format } from 'date-fns';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import { ORPCError } from '@orpc/client';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import {
  VENDOR_PORTAL_UPLOAD_FILE_TYPES,
  type VendorPortalUploadTarget,
} from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCan } from '@/hooks/useCan';
import {
  useUploadVendorPortalFile,
  useVendorPortalUploadTargets,
} from './useVendorPortalUpload';
import {
  EMPTY_UPLOAD_FORM,
  NO_RUN,
  chooseFile,
  chooseVendor,
  editAccount,
  uploadFileProblem,
  uploadInput,
} from './vendorPortalUploadForm';

const RUN_DATETIME_FORMAT = 'MM/dd/yyyy HH:mm';
const ACCEPT = VENDOR_PORTAL_UPLOAD_FILE_TYPES.map((type) => `.${type}`).join(
  ',',
);

function runLabel(triggeredAt: Date): string {
  return format(new Date(triggeredAt), RUN_DATETIME_FORMAT);
}

/**
 * Why the server refused the upload, when it says: a 4xx carries a message written for
 * the user (the wrong file type, a vendor or run that is gone). Anything else says nothing.
 */
function refusalReason(error: unknown): string | undefined {
  return error instanceof ORPCError && error.status < 500 && error.message
    ? error.message
    : undefined;
}

/**
 * "Upload file" on Pricing → Vendor Portals: add one file a vendor portal run missed, for
 * one Vendor Account, instead of re-running the whole workflow. Shown only to users who
 * can create pricing records.
 */
export function VendorPortalUploadButton() {
  const [canCreatePricing] = useCan(Action.Create, Subject.Pricing);
  const [open, setOpen] = useState(false);
  // Owned here, not by the form: the dialog stays open until an upload settles, so a
  // finishing upload can only ever close the form that sent it.
  const upload = useUploadVendorPortalFile();
  if (!canCreatePricing) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <UploadSimpleIcon className="h-4 w-4" />
        <T context="Vendor portals button that opens the dialog to upload a missing file">
          Upload file
        </T>
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!upload.isPending) setOpen(next);
        }}
      >
        <DialogContent className="max-w-md">
          {/* Mounted only while open, so each opening starts from an empty form. */}
          {open ? (
            <UploadForm upload={upload} onDone={() => setOpen(false)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function UploadForm({
  upload,
  onDone,
}: {
  upload: ReturnType<typeof useUploadVendorPortalFile>;
  onDone: () => void;
}) {
  const gt = useGT();
  const [form, setForm] = useState(EMPTY_UPLOAD_FORM);
  const targetsQuery = useVendorPortalUploadTargets(true);
  const targets = targetsQuery.data ?? [];
  const target = targets.find((entry) => entry.workflowId === form.workflowId);
  const input = uploadInput(form);

  async function submit() {
    if (!input) return;
    try {
      await upload.mutateAsync(input);
      toast.success(
        gt('Uploaded {fileName}', {
          context: 'Toast after a file was uploaded on the vendor portals page',
          fileName: input.file.name,
        }),
      );
      onDone();
    } catch (error) {
      const reason = refusalReason(error);
      toast.error(
        gt('Could not upload {fileName}', {
          context:
            'Toast when uploading a file on the vendor portals page fails',
          fileName: input.file.name,
        }),
        reason ? { description: reason } : undefined,
      );
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="Title of the vendor portals upload dialog">
            Upload a vendor portal file
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="Explanation in the vendor portals upload dialog">
            Add a price list file a run missed, for one Vendor Account. It is
            listed with the run&apos;s downloads, marked as uploaded by you.
          </T>
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        {targetsQuery.isError ? (
          <VendorsLoadFailed onRetry={() => void targetsQuery.refetch()} />
        ) : null}
        <VendorSelect
          targets={targets}
          value={form.workflowId}
          onChange={(next) => setForm((current) => chooseVendor(current, next))}
        />
        <div className="space-y-1">
          <Label htmlFor="vendor-portal-upload-file">
            <T context="Vendor portals upload dialog file field label">File</T>
          </Label>
          <Input
            id="vendor-portal-upload-file"
            type="file"
            accept={ACCEPT}
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              setForm((current) => chooseFile(current, file, target));
            }}
          />
          {form.file ? <FileProblem file={form.file} /> : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor="vendor-portal-upload-account">
            <T context="Vendor portals upload dialog account field label">
              Vendor Account number
            </T>
          </Label>
          <Input
            id="vendor-portal-upload-account"
            value={form.accountNumber}
            onChange={(event) =>
              setForm((current) => editAccount(current, event.target.value))
            }
          />
        </div>
        <RunSelect
          target={target}
          value={form.run}
          onChange={(run) => setForm((current) => ({ ...current, run }))}
        />
      </div>
      <DialogFooter>
        <Button variant="ghost" disabled={upload.isPending} onClick={onDone}>
          <T context="Vendor portals upload dialog cancel button">Cancel</T>
        </Button>
        <Button
          disabled={!input || upload.isPending}
          onClick={() => void submit()}
        >
          <T context="Vendor portals upload dialog submit button">Upload</T>
        </Button>
      </DialogFooter>
    </>
  );
}

function VendorSelect({
  targets,
  value,
  onChange,
}: {
  targets: VendorPortalUploadTarget[];
  value: string | null;
  onChange: (target: VendorPortalUploadTarget) => void;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label>
        <T context="Vendor portals upload dialog vendor field label">Vendor</T>
      </Label>
      <Select
        value={value ?? ''}
        onValueChange={(next) => {
          const chosen = targets.find((entry) => entry.workflowId === next);
          if (chosen) onChange(chosen);
        }}
      >
        <SelectTrigger
          aria-label={gt('Vendor', {
            context: 'Vendor portals upload dialog vendor field label',
          })}
        >
          <SelectValue
            placeholder={gt('Choose a vendor', {
              context: 'Vendor portals upload dialog vendor field placeholder',
            })}
          >
            {targets.find((entry) => entry.workflowId === value)?.vendor}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {targets.map((entry) => (
            <SelectItem key={entry.workflowId} value={entry.workflowId}>
              {entry.vendor}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function RunSelect({
  target,
  value,
  onChange,
}: {
  target: VendorPortalUploadTarget | undefined;
  value: string;
  onChange: (run: string) => void;
}) {
  const gt = useGT();
  const runs = target?.runs ?? [];
  const noRun = gt('No run', {
    context: 'Vendor portals upload dialog option to attach the file to no run',
  });
  const chosen = runs.find((run) => run.workflowRunId === value);
  return (
    <div className="space-y-1">
      <Label>
        <T context="Vendor portals upload dialog run field label">
          Workflow run
        </T>
      </Label>
      <Select
        value={value}
        disabled={!target}
        onValueChange={(next) => next && onChange(next)}
      >
        <SelectTrigger
          aria-label={gt('Workflow run', {
            context: 'Vendor portals upload dialog run field label',
          })}
        >
          <SelectValue>
            {chosen ? runLabel(chosen.triggeredAt) : noRun}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {runs.map((run) => (
            <SelectItem key={run.workflowRunId} value={run.workflowRunId}>
              {runLabel(run.triggeredAt)}
            </SelectItem>
          ))}
          <SelectItem value={NO_RUN}>{noRun}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function VendorsLoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex items-center gap-2 text-sm text-destructive">
      <T context="Vendor portals upload dialog: the list of vendors failed to load">
        Could not load the vendors.
      </T>
      <Button size="sm" variant="outline" onClick={onRetry}>
        <T context="Vendor portals upload dialog button that reloads the vendors">
          Retry
        </T>
      </Button>
    </div>
  );
}

/** Says why a picked file can't be uploaded, before the user sends it. */
function FileProblem({ file }: { file: File }) {
  const problem = uploadFileProblem(file);
  if (!problem) return null;
  return (
    <p className="text-sm text-destructive">
      {problem === 'empty' ? (
        <T context="Vendor portals upload dialog: the picked file has no content">
          That file is empty.
        </T>
      ) : (
        <T context="Vendor portals upload dialog: the picked file is over the upload size limit">
          That file is too large to upload (100 MB at most).
        </T>
      )}
    </p>
  );
}
